import emailjs from "@emailjs/browser";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, Camera, CheckCircle2, Send, Sparkles } from "lucide-react";
import { useState } from "react";
// We'll reuse the scss file since it's already imported in the parent,
// but importing it here is safe too or we can assume styles are global/scoped by parent class.
// import './palyazat.scss';

// TODO: Replace with your actual Gemini API Key
const GEMINI_API_KEY = "AIzaSyDKkWvOLjCROorVt8BoDITzhR62PH4nKEE";

const MultiStepContactForm = ({ onSuccess }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 2 (Contact)
    name: "",
    zip: "",
    city: "",
    address: "",
    hrs: "",
    email: "",
    phone: "",
    hasSolar: "yes", // Defaulted to yes if they pass step 1

    // Step 3 (Technical)
    annualConsumption: "",
    connectionCapacity: "",
    hasHybridInverter: "",
    has3Phase: "",
    dataPlateImage: null,

    // Step 4 (Photos)
    installLocationImage: null,
    inverterImage: null,
  });

  // Step 1: Eligibility State
  const [eligibility, setEligibility] = useState({
    isPrivatePerson: null, // null, 'yes', 'no'
    isPermanentResidence: null,
    hasOwnership: null,
    isHouse: null,
    isSmallApartment: null, // New: for max 6-unit apartment buildings
    hasExistingSolar: null,
    hasDap: null,
    requestsAdministration: null,
  });

  // AI Helper State
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [isAiLoading, setIsAiLoading] = useState(false);

  // Analysis State
  const [analyzing, setAnalyzing] = useState({}); // Stores bool for each type e.g., { location: true }
  const [analysisResults, setAnalysisResults] = useState({}); // Stores result for each type

  // Deprecated/Refactored single state (removed)
  // const [analyzing, setAnalyzing] = useState(false);
  // const [analysisResult, setAnalysisResult] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleEligibilityChange = (key, value) => {
    setEligibility((prev) => ({ ...prev, [key]: value }));
    // Sync existing solar to formData if explicitly asked
    if (key === "hasExistingSolar") {
      setFormData((prev) => ({ ...prev, hasSolar: value }));
    }
  };

  const handleAskAi = async () => {
    if (!aiQuestion.trim()) return;
    setIsAiLoading(true);
    try {
      await runGeminiAnalysis(null, "text", aiQuestion);
    } catch (e) {
      console.error(e);
    } finally {
      setIsAiLoading(false);
    }
  };

  const fileToGenerativePart = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        resolve({
          inlineData: {
            data: base64String,
            mimeType: file.type,
          },
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e, fieldName) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData((prev) => ({ ...prev, [fieldName]: file }));

      // Trigger analysis if it's the data plate
      if (fieldName === "dataPlateImage") {
        await runGeminiAnalysis(file, "dataPlate");
      }
      // Trigger analysis for other images if needed
      if (fieldName === "inverterImage") {
        await runGeminiAnalysis(file, "inverter");
      }
      if (fieldName === "installLocationImage") {
        await runGeminiAnalysis(file, "location");
      }
    }
  };

  const runGeminiAnalysis = async (file, type, textQuery = "") => {
    if (!GEMINI_API_KEY || GEMINI_API_KEY === "YOUR_GEMINI_API_KEY_HERE") {
      console.warn("Gemini API Key is missing. Skipping analysis.");
      return;
    }

    if (type !== "text") {
      const key = type === "dataPlate" ? "dataPlate" : type;
      setAnalyzing((prev) => ({ ...prev, [key]: true }));
      // Clear previous result for this specific type
      setAnalysisResults((prev) => ({ ...prev, [key]: null }));
    }

    try {
      let prompt = "";
      let payloadParts = [];

      if (type === "text") {
        prompt = `You are a helpful assistant for a Hungarian Home Energy Storage government grant program (Otthoni Energiatároló Támogatás / Napenergia Plusz Program jellegű).
         User question: "${textQuery}".
         
         Context knowledge:
         - Eligibility: Private individuals (magánszemély), Permanent residence (állandó lakcím/tartózkodási hely), Ownership (tulajdonjog), Family house/semi-detached/row house (családi ház, ikerház, sorház).
         - Existing solar system (működő napelem) is NOT required for eligibility (new system installation is also supported).
         - Not eligible: Apartment blocks (társasház) generally, Business properties.
         
         Answer shortly and clearly in Hungarian. Do NOT use markdown formatting (no bold, no italics, no bullet points). Use simple text and newlines for separation. Is they eligible based on their question?`;
        payloadParts = [{ text: prompt }];
      } else {
        const imagePart = await fileToGenerativePart(file);
        if (type === "dataPlate") {
          prompt =
            'Analyze this image of a solar inverter data plate. Check if it explicitly mentions \'Hybrid\' or technical specifications indicating hybrid capability (like battery connection). Answer in JSON format: { "isHybrid": boolean, "confidence": number (0-1), "reasoning": "string in Hungarian" }';
        } else if (type === "inverter") {
          prompt =
            'Analyze this image. Is this a solar inverter? Answer in JSON format: { "isInverter": boolean, "reasoning": "string in Hungarian" }';
        } else if (type === "location") {
          prompt =
            'Analyze this image. Is this a valid location for solar panel installation (e.g., roof, ground)? Answer in JSON format: { "isLocation": boolean, "reasoning": "string in Hungarian" }';
        }
        payloadParts = [{ text: prompt }, imagePart];
      }

      const payload = {
        contents: [
          {
            parts: payloadParts,
          },
        ],
        generationConfig: {
          // Only force JSON for image analysis part, text is free text
          responseMimeType: type === "text" ? "text/plain" : "application/json",
        },
      };

      const response = await axios.post(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`,
        payload
      );

      const resultText =
        response.data?.candidates?.[0]?.content?.parts?.[0]?.text;

      if (resultText) {
        if (type === "text") {
          setAiResponse(resultText);
        } else {
          const resultJson = JSON.parse(resultText);
          let resultMessage = null;

          if (type === "dataPlate") {
            if (resultJson.isHybrid) {
              resultMessage = {
                type: "success",
                message: `Hybrid Inverter észlelve (${resultJson.reasoning})`,
              };
            } else {
              resultMessage = {
                type: "info",
                message: `Nem tűnik Hybridnek (${resultJson.reasoning})`,
              };
            }
          } else if (type === "location" || type === "inverter") {
            resultMessage = {
              type: "success",
              message: `${resultJson.reasoning}`,
            };
          }

          if (resultMessage) {
            setAnalysisResults((prev) => ({ ...prev, [type]: resultMessage }));
          }
        }
      }
    } catch (error) {
      console.error("Gemini analysis failed:", error);
      if (type !== "text") {
        setAnalysisResults((prev) => ({
          ...prev,
          [type]: { type: "error", message: "Hiba a kép elemzése során." },
        }));
      } else {
        setAiResponse("Hiba történt a válaszadás során. Próbálja újra.");
      }
    } finally {
      if (type !== "text") {
        setAnalyzing((prev) => ({ ...prev, [type]: false }));
      }
    }
  };

  const validateStep1_Eligibility = () => {
    // All must be yes
    const {
      isPrivatePerson,
      isPermanentResidence,
      hasOwnership,
      isHouse,
      isSmallApartment,
      hasExistingSolar,
      hasDap,
      requestsAdministration,
    } = eligibility;

    // Check hard disqualifiers
    if (
      isPrivatePerson === "no" ||
      isPermanentResidence === "no" ||
      hasOwnership === "no"
    ) {
      return false; // Ineligible
    }

    // House type check: either isHouse=yes OR (isHouse=no AND isSmallApartment=yes)
    const validHouseType =
      isHouse === "yes" || (isHouse === "no" && isSmallApartment === "yes");
    if (isHouse === "no" && isSmallApartment === "no") {
      return false; // Large apartment building - ineligible
    }

    return (
      isPrivatePerson === "yes" &&
      isPermanentResidence === "yes" &&
      hasOwnership === "yes" &&
      validHouseType &&
      (hasExistingSolar === "yes" || hasExistingSolar === "no") &&
      (hasDap === "yes" || hasDap === "no") &&
      (requestsAdministration === "yes" || requestsAdministration === "no")
    );
  };

  const validateStep2_Contact = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = true;
    if (!formData.zip) newErrors.zip = true;
    if (!formData.city) newErrors.city = true;
    if (!formData.address) newErrors.address = true;
    if (!formData.hrs) newErrors.hrs = true;
    if (!formData.email) newErrors.email = true;
    if (!formData.phone) newErrors.phone = true;
    // hasSolar has default 'no', so it should be fine, but good to check if changed
    if (!formData.hasSolar) newErrors.hasSolar = true;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3_Tech = () => {
    const newErrors = {};
    if (!formData.annualConsumption) newErrors.annualConsumption = true;
    if (!formData.connectionCapacity) newErrors.connectionCapacity = true;
    if (!formData.hasHybridInverter) newErrors.hasHybridInverter = true;
    if (!formData.has3Phase) newErrors.has3Phase = true;
    if (
      formData.hasHybridInverter === "dont_know" &&
      !formData.dataPlateImage
    ) {
      newErrors.dataPlateImage = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep4_Photos = () => {
    const newErrors = {};
    if (!formData.installLocationImage) newErrors.installLocationImage = true;
    // Only require inverter photo if user has existing solar system
    if (eligibility.hasExistingSolar === "yes" && !formData.inverterImage) {
      newErrors.inverterImage = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    let isValid = false;
    if (step === 1) {
      isValid = validateStep1_Eligibility();
      if (!isValid) {
        // Maybe show a modal or message?
        // For now, the button logic handles disabling, but if they click, check again
        alert(
          "Sajnáljuk, a megadott válaszok alapján Ön jelenleg nem tűnik jogosultnak a pályázatra (pl. nem magánszemély)."
        );
        return;
      }
    } else if (step === 2) isValid = validateStep2_Contact();
    else if (step === 3) isValid = validateStep3_Tech();

    if (isValid) {
      // Clear previous analysis results if desired, or keep them.
      // Original code cleared them, so we can reset or just leave them.
      // To mimic original behavior of clearing the single result:
      // setAnalysisResults({});
      // However, usually it's better to keep them if user goes back?
      // Let's clear them to avoid confusion if they change previous inputs?
      // Actually, let's just NOT clear them, so the state persists for the step.
      // But the error is that setAnalysisResult is not defined. I should just remove the call.
      setStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    // setAnalysisResults({}); // Optional: clear on back
    setErrors({}); // Clear errors when going back
    setStep((prev) => prev - 1);
  };

  const handleFinalSubmit = (e) => {
    e.preventDefault();
    if (validateStep4_Photos()) {
      handleSubmit(e);
    }
  };

  const handleSubmit = (e) => {
    // e.preventDefault(); // Called from handleFinalSubmit which prevents default
    setIsSubmitting(true);
    setSubmitError(null);

    // Format current date for submission timestamp
    const now = new Date();
    const submissionDate = now.toLocaleDateString("hu-HU", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Prepare template params with all collected data
    const templateParams = {
      // Submission Info
      submission_date: submissionDate,

      // Eligibility Answers
      isPrivatePerson: eligibility.isPrivatePerson === "yes" ? "Igen" : "Nem",
      isPermanentResidence:
        eligibility.isPermanentResidence === "yes" ? "Igen" : "Nem",
      hasOwnership: eligibility.hasOwnership === "yes" ? "Igen" : "Nem",
      isHouse: eligibility.isHouse === "yes" ? "Igen" : "Nem",
      isSmallApartment:
        eligibility.isHouse === "no"
          ? eligibility.isSmallApartment === "yes"
            ? "Igen (max 6 lakásos társasház)"
            : "Nem"
          : "N/A (családi ház)",
      hasExistingSolar: eligibility.hasExistingSolar === "yes" ? "Igen" : "Nem",
      hasDap: eligibility.hasDap === "yes" ? "Igen" : "Nem",
      requestsAdministration:
        eligibility.requestsAdministration === "yes" ? "Igen" : "Nem",

      // Personal Info
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      zip: formData.zip,
      city: formData.city,
      address: formData.address,
      full_address: `${formData.zip} ${formData.city}, ${formData.address}`,
      hrs: formData.hrs,

      // Technical Info
      annualConsumption: formData.annualConsumption || "Nem adott meg",
      connectionCapacity: formData.connectionCapacity || "Nem adott meg",
      hasSolar: eligibility.hasExistingSolar === "yes" ? "Van" : "Nincs",
      hasHybridInverter:
        formData.hasHybridInverter === "yes"
          ? "Igen"
          : formData.hasHybridInverter === "no"
          ? "Nem"
          : "Nem tudom",
      has3Phase:
        formData.has3Phase === "yes"
          ? "Igen"
          : formData.has3Phase === "no"
          ? "Nem"
          : "Nem tudom",

      // File Status Indicators
      uploaded_data_plate: formData.dataPlateImage
        ? "Csatolva"
        : "Nincs csatolva",
      uploaded_location: formData.installLocationImage
        ? "Csatolva"
        : "Nincs csatolva",
      uploaded_inverter: formData.inverterImage ? "Csatolva" : "Nincs csatolva",

      // Property Type Summary
      property_type:
        eligibility.isHouse === "yes"
          ? "Családi ház / Ikerház / Sorház"
          : eligibility.isSmallApartment === "yes"
          ? "Társasház (max 6 lakás)"
          : "Egyéb",
    };

    emailjs
      .send(
        "service_op1bncr",
        "template_vuij4yl",
        templateParams,
        "qJvTu124RTxQEf_G2"
      )
      .then((response) => {
        console.log("SUCCESS!", response.status, response.text);
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        console.log("FAILED...", err);
        setSubmitError("Hiba történt a küldés során. Kérjük, próbálja újra.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const stepVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3 } },
  };

  return (
    <div className="multi-step-form-container">
      {/* Progress Indicator */}
      <div className="step-indicator">
        <div className={`step-dot ${step >= 1 ? "active" : ""}`}>1</div>
        <div className="step-line"></div>
        <div className={`step-dot ${step >= 2 ? "active" : ""}`}>2</div>
        <div className="step-line"></div>
        <div className={`step-dot ${step >= 3 ? "active" : ""}`}>3</div>
        <div className="step-line"></div>
        <div className={`step-dot ${step >= 4 ? "active" : ""}`}>4</div>
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            variants={stepVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="form-step">
            <h4>Jogosultság Ellenőrzése</h4>
            <p className="step-desc">
              Kérjük, válaszoljon az alábbi kérdésekre, hogy ellenőrizzük
              jogosultságát a pályázatra.
            </p>

            <div className="eligibility-questions">
              {[
                {
                  key: "isPrivatePerson",
                  question: "Magánszemélyként pályázik?",
                },
                {
                  key: "isPermanentResidence",
                  question:
                    "Van állandó lakcíme vagy tartózkodási helye az ingatlanban?",
                },
                {
                  key: "hasOwnership",
                  question: "Rendelkezik tulajdonjoggal az ingatlanban?",
                },
                {
                  key: "isHouse",
                  question: "Az ingatlan családi ház, ikerház vagy sorház?",
                },
                // Conditional question for apartment buildings - rendered separately below
                {
                  key: "hasExistingSolar",
                  question: "Rendelkezik már működő napelemes rendszerrel?",
                },
                {
                  key: "hasDap",
                  question:
                    "Rendelkezik Digitális Állampolgár (DÁP) mobilalkalmazáson keresztül elérhető minősített elektronikus aláírással?",
                },
                {
                  key: "requestsAdministration",
                  question:
                    "Kéri, hogy a Green Island Invest az Ön nevében elvégezze a pályázati regisztrációt és a pályázat benyújtását?",
                },
              ].map((item) => {
                return (
                  <div
                    className="form-group row-group"
                    key={item.key}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "1rem",
                      borderBottom: "1px solid #ffffff1a",
                      paddingBottom: "0.5rem",
                    }}>
                    <label style={{ marginBottom: 0, flex: 1 }}>
                      {item.question}
                    </label>
                    <div className="radio-group" style={{ flex: "0 0 auto" }}>
                      <label
                        className={`radio-btn small ${
                          eligibility[item.key] === "yes" ? "selected" : ""
                        }`}>
                        <input
                          type="radio"
                          name={item.key}
                          value="yes"
                          checked={eligibility[item.key] === "yes"}
                          onChange={() =>
                            handleEligibilityChange(item.key, "yes")
                          }
                        />{" "}
                        Igen
                      </label>
                      <label
                        className={`radio-btn small ${
                          eligibility[item.key] === "no" ? "selected" : ""
                        }`}>
                        <input
                          type="radio"
                          name={item.key}
                          value="no"
                          checked={eligibility[item.key] === "no"}
                          onChange={() =>
                            handleEligibilityChange(item.key, "no")
                          }
                        />{" "}
                        Nem
                      </label>
                    </div>
                  </div>
                );
              })}

              {/* Conditional follow-up question for apartment buildings */}
              {eligibility.isHouse === "no" && (
                <div
                  className="form-group row-group"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1rem",
                    borderBottom: "1px solid #ffffff1a",
                    paddingBottom: "0.5rem",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    padding: "0.75rem",
                    borderRadius: "8px",
                    marginTop: "-0.5rem",
                  }}>
                  <label style={{ marginBottom: 0, flex: 1 }}>
                    Legfeljebb 6 lakásos társasház az ingatlan?
                  </label>
                  <div className="radio-group" style={{ flex: "0 0 auto" }}>
                    <label
                      className={`radio-btn small ${
                        eligibility.isSmallApartment === "yes" ? "selected" : ""
                      }`}>
                      <input
                        type="radio"
                        name="isSmallApartment"
                        value="yes"
                        checked={eligibility.isSmallApartment === "yes"}
                        onChange={() =>
                          handleEligibilityChange("isSmallApartment", "yes")
                        }
                      />{" "}
                      Igen
                    </label>
                    <label
                      className={`radio-btn small ${
                        eligibility.isSmallApartment === "no" ? "selected" : ""
                      }`}>
                      <input
                        type="radio"
                        name="isSmallApartment"
                        value="no"
                        checked={eligibility.isSmallApartment === "no"}
                        onChange={() =>
                          handleEligibilityChange("isSmallApartment", "no")
                        }
                      />{" "}
                      Nem
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: "2rem" }}>
              {!validateStep1_Eligibility() &&
                (eligibility.isPrivatePerson === "no" ||
                  eligibility.isPermanentResidence === "no" ||
                  eligibility.hasOwnership === "no" ||
                  (eligibility.isHouse === "no" &&
                    eligibility.isSmallApartment === "no")) && (
                  <div
                    className="error-message"
                    style={{ marginBottom: "1rem" }}>
                    {eligibility.isHouse === "no" &&
                    eligibility.isSmallApartment === "no"
                      ? "Sajnos 6 lakásnál nagyobb társasház esetén nem lehet pályázni."
                      : "A megadott válaszok alapján Ön sajnos nem felel meg a pályázati feltételeknek (pl. nem magánszemély, nem lakóingatlan)."}
                  </div>
                )}
              <button
                className={`btn-primary w-100 ${
                  validateStep1_Eligibility() ? "step-valid" : "step-incomplete"
                }`}
                onClick={handleNext}
                disabled={!validateStep1_Eligibility()}>
                Tovább az adatok megadásához
              </button>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="step2"
            initial="hidden"
            animate="visible"
            exit="exit"
            className="form-step">
            <div className="form-header-nav">
              <button className="btn-back-link" onClick={prevStep}>
                ← Vissza
              </button>
            </div>
            <h4>Személyes adatok</h4>
            <div className="form-group">
              <label>Név</label>
              <input
                type="text"
                name="name"
                className={errors.name ? "input-error" : ""}
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Pl. Kovács János"
              />
              {errors.name && (
                <span className="error-text">A név megadása kötelező.</span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group half">
                <label>Irányítószám</label>
                <input
                  type="text"
                  name="zip"
                  className={errors.zip ? "input-error" : ""}
                  value={formData.zip}
                  onChange={handleInputChange}
                  placeholder="Pl. 1234"
                />
                {errors.zip && <span className="error-text">Kötelező.</span>}
              </div>
              <div className="form-group half">
                <label>Település</label>
                <input
                  type="text"
                  name="city"
                  className={errors.city ? "input-error" : ""}
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Pl. Budapest"
                />
                {errors.city && <span className="error-text">Kötelező.</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Utca, házszám</label>
              <input
                type="text"
                name="address"
                className={errors.address ? "input-error" : ""}
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Pl. Petőfi Sándor utca 12."
              />
              {errors.address && (
                <span className="error-text">A cím megadása kötelező.</span>
              )}
            </div>

            <div className="form-group">
              <label>Helyrajzi szám</label>
              <input
                type="text"
                name="hrs"
                className={errors.hrs ? "input-error" : ""}
                value={formData.hrs}
                onChange={handleInputChange}
                placeholder="Pl. 12345/6"
              />
              {errors.hrs && (
                <span className="error-text">
                  A helyrajzi szám megadása kötelező.
                </span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group half">
                <label>Telefonszám</label>
                <input
                  type="tel"
                  name="phone"
                  className={errors.phone ? "input-error" : ""}
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+36 30 123 4567"
                />
                {errors.phone && <span className="error-text">Kötelező.</span>}
              </div>
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                className={errors.email ? "input-error" : ""}
                value={formData.email}
                onChange={handleInputChange}
                placeholder="kovacs.janos@email.com"
              />
              {errors.email && (
                <span className="error-text">
                  Az email cím megadása kötelező.
                </span>
              )}
            </div>
            {/* Solar question removed as it is checked in Step 1 */}

            <button
              className={`btn-primary w-100 ${
                formData.name &&
                formData.zip &&
                formData.city &&
                formData.address &&
                formData.hrs &&
                formData.email &&
                formData.phone
                  ? "step-valid"
                  : "step-incomplete"
              }`}
              onClick={handleNext}>
              Tovább a 3. lépésre
            </button>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div
            key="step3"
            variants={stepVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="form-step">
            <div className="form-header-nav">
              <button className="btn-back-link" onClick={prevStep}>
                ← Vissza
              </button>
            </div>

            <h4>Műszaki adatok</h4>

            <div className="form-group">
              <label>Éves áramfogyasztás (kWh)</label>
              <input
                type="number"
                name="annualConsumption"
                className={errors.annualConsumption ? "input-error" : ""}
                value={formData.annualConsumption}
                onChange={handleInputChange}
                placeholder="Pl. 4500"
              />
              {errors.annualConsumption && (
                <span className="error-text">
                  Megadása kötelező az akkumulátor méretezéséhez.
                </span>
              )}
            </div>

            <div className="form-group">
              <label>Csatlakozási teljesítmény (Amper)</label>
              <input
                type="text"
                name="connectionCapacity"
                className={errors.connectionCapacity ? "input-error" : ""}
                value={formData.connectionCapacity}
                onChange={handleInputChange}
                placeholder="Pl. 3x16A vagy 1x32A"
              />
              {errors.connectionCapacity && (
                <span className="error-text">Megadása kötelező.</span>
              )}
            </div>

            <div className="form-group">
              <label>Van-e hybrid invertere?</label>
              <div
                className={`radio-group vertical ${
                  errors.hasHybridInverter ? "input-error-border" : ""
                }`}>
                <label
                  className={`radio-btn ${
                    formData.hasHybridInverter === "yes" ? "selected" : ""
                  }`}>
                  <input
                    type="radio"
                    name="hasHybridInverter"
                    value="yes"
                    checked={formData.hasHybridInverter === "yes"}
                    onChange={handleInputChange}
                  />
                  Igen
                </label>
                <label
                  className={`radio-btn ${
                    formData.hasHybridInverter === "no" ? "selected" : ""
                  }`}>
                  <input
                    type="radio"
                    name="hasHybridInverter"
                    value="no"
                    checked={formData.hasHybridInverter === "no"}
                    onChange={handleInputChange}
                  />
                  Nem
                </label>
                <label
                  className={`radio-btn ${
                    formData.hasHybridInverter === "dont_know" ? "selected" : ""
                  }`}>
                  <input
                    type="radio"
                    name="hasHybridInverter"
                    value="dont_know"
                    checked={formData.hasHybridInverter === "dont_know"}
                    onChange={handleInputChange}
                  />
                  Nem tudom (Fénykép feltöltése)
                </label>
              </div>
              {errors.hasHybridInverter && (
                <span className="error-text">Kérjük válasszon egy opciót.</span>
              )}
            </div>

            {formData.hasHybridInverter === "dont_know" && (
              <div className="form-group upload-group">
                <label>Kérjük, fotózza le az inverter adattábláját</label>
                <div
                  className={`file-upload-wrapper ${
                    errors.dataPlateImage ? "input-error-border" : ""
                  }`}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "dataPlateImage")}
                  />
                  <div className="upload-placeholder">
                    {formData.dataPlateImage ? (
                      <span
                        className="file-name"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                        }}>
                        <CheckCircle2 size={18} />{" "}
                        {formData.dataPlateImage.name}
                      </span>
                    ) : (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                        }}>
                        <Camera size={20} /> Koppintson a fotózáshoz vagy
                        feltöltéshez
                      </span>
                    )}
                  </div>
                </div>
                {errors.dataPlateImage && (
                  <span className="error-text">
                    A fénykép feltöltése kötelező ennél az opciónál.
                  </span>
                )}
                {analyzing.dataPlate && (
                  <p className="analysis-status">
                    <Sparkles size={18} /> Elemezzük a képet...
                  </p>
                )}
                {analysisResults.dataPlate && (
                  <p className="analysis-success">
                    {analysisResults.dataPlate.message}
                  </p>
                )}
              </div>
            )}

            <div className="form-group">
              <label>Ki van-e építve a háromfázisú hálózat?</label>
              <div
                className={`radio-group ${
                  errors.has3Phase ? "input-error-border" : ""
                }`}>
                <label
                  className={`radio-btn ${
                    formData.has3Phase === "yes" ? "selected" : ""
                  }`}>
                  <input
                    type="radio"
                    name="has3Phase"
                    value="yes"
                    checked={formData.has3Phase === "yes"}
                    onChange={handleInputChange}
                  />
                  Igen
                </label>
                <label
                  className={`radio-btn ${
                    formData.has3Phase === "no" ? "selected" : ""
                  }`}>
                  <input
                    type="radio"
                    name="has3Phase"
                    value="no"
                    checked={formData.has3Phase === "no"}
                    onChange={handleInputChange}
                  />
                  Nem
                </label>
                <label
                  className={`radio-btn ${
                    formData.has3Phase === "dont_know" ? "selected" : ""
                  }`}>
                  <input
                    type="radio"
                    name="has3Phase"
                    value="dont_know"
                    checked={formData.has3Phase === "dont_know"}
                    onChange={handleInputChange}
                  />
                  Nem tudom
                </label>
              </div>
              {errors.has3Phase && (
                <span className="error-text">Kérjük válasszon egy opciót.</span>
              )}
            </div>

            <button
              className={`btn-primary w-100 ${
                formData.annualConsumption &&
                formData.connectionCapacity &&
                formData.hasHybridInverter &&
                formData.has3Phase &&
                !(
                  formData.hasHybridInverter === "dont_know" &&
                  !formData.dataPlateImage
                )
                  ? "step-valid"
                  : "step-incomplete"
              }`}
              onClick={handleNext}
              disabled={Object.values(analyzing).some(Boolean)}>
              Tovább a 4. lépésre
            </button>
          </motion.div>
        )}

        {step === 4 && (
          <motion.div
            key="step4"
            variants={stepVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="form-step">
            <div className="form-header-nav">
              <button className="btn-back-link" onClick={prevStep}>
                ← Vissza
              </button>
            </div>

            <h4>Helyszín{eligibility.hasExistingSolar === "yes" ? " és Inverter" : ""} fotók</h4>
            <p className="step-desc">
              Az AI segítségével ellenőrizzük a telepítési helyszín
              alkalmasságát.
            </p>

            <div className="form-group upload-group">
              <label>Telepítés helye (fotó)</label>
              <div
                className={`file-upload-wrapper ${
                  errors.installLocationImage ? "input-error-border" : ""
                }`}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, "installLocationImage")}
                />
                <div className="upload-placeholder">
                  {formData.installLocationImage ? (
                    <span
                      className="file-name"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                      }}>
                      <CheckCircle2 size={18} />{" "}
                      {formData.installLocationImage.name}
                    </span>
                  ) : (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                      }}>
                      <Camera size={20} /> Helyszín fotózása
                    </span>
                  )}
                </div>
              </div>
              {errors.installLocationImage && (
                <span className="error-text">
                  Kérjük töltse fel a helyszín fotóját.
                </span>
              )}
              {analyzing.location && (
                <p className="analysis-status">
                  <Sparkles size={18} /> Elemzzük a képet...
                </p>
              )}
              {analysisResults.location && (
                <p className="analysis-success">
                  {analysisResults.location.message}
                </p>
              )}
            </div>

            {/* Only show inverter photo upload if user has existing solar system */}
            {eligibility.hasExistingSolar === "yes" && (
              <div className="form-group upload-group">
                <label>Inverter (fotó)</label>
                <div
                  className={`file-upload-wrapper ${
                    errors.inverterImage ? "input-error-border" : ""
                  }`}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "inverterImage")}
                  />
                  <div className="upload-placeholder">
                    {formData.inverterImage ? (
                      <span
                        className="file-name"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                        }}>
                        <CheckCircle2 size={18} /> {formData.inverterImage.name}
                      </span>
                    ) : (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                        }}>
                        <Camera size={20} /> Inverter fotózása
                      </span>
                    )}
                  </div>
                </div>
                {errors.inverterImage && (
                  <span className="error-text">
                    Kérjük töltse fel az inverter fotóját.
                  </span>
                )}
                {analyzing.inverter && (
                  <p className="analysis-status">
                    <Sparkles size={18} /> Elemezzük a képet...
                  </p>
                )}
                {analysisResults.inverter && (
                  <p className="analysis-success">
                    {analysisResults.inverter.message}
                  </p>
                )}
              </div>
            )}

            {submitError && <div className="error-message">{submitError}</div>}

            <button
              className={`submit-btn w-100 ${
                formData.installLocationImage && 
                (eligibility.hasExistingSolar === "no" || formData.inverterImage)
                  ? "step-valid"
                  : "step-incomplete"
              }`}
              onClick={handleFinalSubmit}
              disabled={isSubmitting}>
              {isSubmitting ? "Küldés..." : "Jelentkezés beküldése"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Helper Section - Always Visible */}
      <div
        className="ai-helper-section"
        style={{
          marginTop: "2rem",
          borderTop: "1px solid rgba(0,0,0,0.1)",
          paddingTop: "2rem",
        }}>
        <div className="ai-header">
          <Bot size={20} />
          <span>Pályázati AI Asszisztens</span>
        </div>
        <p className="ai-description">
          Kérdése van a pályázattal kapcsolatban? Kérdezze meg mesterséges
          intelligencia asszisztensünket!
        </p>

        <div className="ai-input-group">
          <input
            type="text"
            placeholder="Pl. Nyaralóra lehet pályázni?"
            value={aiQuestion}
            onChange={(e) => setAiQuestion(e.target.value)}
            onKeyPress={(e) =>
              e.key === "Enter" && !isAiLoading && handleAskAi()
            }
            disabled={isAiLoading}
          />
          <button
            onClick={handleAskAi}
            disabled={isAiLoading || !aiQuestion}
            className="btn-ai-submit">
            {isAiLoading ? (
              <Sparkles
                className="spin"
                color="white"
                strokeWidth={2}
                size={20}
              />
            ) : (
              <Send color="white" strokeWidth={2} size={20} />
            )}
          </button>
        </div>
        <AnimatePresence>
          {aiResponse && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="ai-response">
              <strong>Válasz:</strong>
              <div style={{ whiteSpace: "pre-wrap" }}>{aiResponse}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MultiStepContactForm;
